/** Production pass for one building. Grow → product, Deal → cash (to the shared
 *  city bankroll). Lab/Pack are optional staffed boosters. Basements draw less HEAT. */
import type { City, Building, Colonist, Room, SkillKey } from './types';
import { SIM, roomDef, isNight, districtMeta } from './config';
import { workMul, isNightWorker } from './colonist';

const DRUG = new Set(['grow', 'deal']);

function gainXp(c: Colonist, skill: SkillKey, dt: number): void {
  const s = c.skills[skill];
  if (s.level >= SIM.SKILL_MAX) return;
  s.xp += SIM.XP_PER_SEC * dt;
  const need = SIM.XP_PER_LEVEL * (s.level + 1);
  if (s.xp >= need) {
    s.xp -= need;
    s.level += 1;
  }
}

function nightFactor(now: number, c: Colonist): number {
  return isNight(now) && !isNightWorker(c) ? 0.85 : 1;
}

export function workersOf(b: Building, room: Room): Colonist[] {
  return b.colonists.filter((c) => c.assignedRoomId === room.id && c.activity === 'work');
}

function staffed(b: Building, type: Room['type'], now: number): boolean {
  return b.rooms.some((r) => r.type === type && r.damagedUntil <= now && workersOf(b, r).length > 0);
}

export function runProduction(city: City, b: Building, dt: number): void {
  const now = city.t;
  const fresh = city.activeEvents.some((e) => e.id === 'fresh-batch') ? 1.5 : 1;
  const rush = city.activeEvents.some((e) => e.id === 'customer-rush') ? 2 : 1;
  const valueMul = staffed(b, 'lab', now) ? SIM.LAB_VALUE_MUL : 1;
  const throughputMul = staffed(b, 'pack', now) ? SIM.PACK_THROUGHPUT_MUL : 1;
  const dist = districtMeta(b.district);

  if (b.rooms.some((r) => r.type === 'diner' && r.damagedUntil <= now)) {
    b.res.meals += SIM.DINER_TRICKLE * dt;
  }

  for (const room of b.rooms) {
    const def = roomDef(room.type);
    if (!def.workplace || room.damagedUntil > now) {
      room.progress = 0;
      continue;
    }
    if (b.layingLow && DRUG.has(room.type)) {
      room.progress = 0;
      continue;
    }
    const workers = workersOf(b, room);
    if (workers.length === 0) {
      room.progress = 0;
      continue;
    }

    let power = 0;
    for (const c of workers) {
      const sk = def.skill ? SIM.skillMult(c.skills[def.skill].level) : 1;
      power += sk * workMul(c) * nightFactor(now, c);
      if (def.skill) gainXp(c, def.skill, dt);
    }
    power *= SIM.tierMult(room.tier);
    room.progress = Math.min(1, power / workers.length / 2);

    const baseRate = (def.rate ?? 0) * power * fresh * dt;
    const heatMul = room.floor < 0 ? SIM.BASEMENT_HEAT_MUL : 1;

    switch (room.type) {
      case 'grow': {
        const made = baseRate * throughputMul;
        b.res.product += made;
        b.heat += made * (def.heatPerUnit ?? 0) * heatMul * dist.heat;
        break;
      }
      case 'deal': {
        const sold = Math.min(baseRate, b.res.product);
        b.res.product -= sold;
        const gain = sold * (def.sellPrice ?? 0) * rush * valueMul * (1 + city.rep) * dist.demand;
        city.cash += gain;
        city.runEarned += gain;
        b.heat += sold * (def.heatPerUnit ?? 0) * heatMul * dist.heat;
        break;
      }
      case 'diner': {
        const byCash = (def.cashPerUnit ?? 0) > 0 ? city.cash / (def.cashPerUnit ?? 1) : baseRate;
        const made = Math.min(baseRate, byCash);
        city.cash -= made * (def.cashPerUnit ?? 0);
        b.res.meals += made;
        break;
      }
    }
  }
}
